import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit'
import { PlayInObject, PlayInStatus, RemoteStatusRequestInterface } from '../../../lib/playIn/types'
import { CommunicationType, CommunicationRequest } from '../../../services/apiGateway/apiGatewayTypes'
import { State } from '../../reducers/reducerType'
import { addEntry } from '../../../lib/audit'
import get from 'lodash/get'
import has from 'lodash/has'
import isEqual from 'lodash/isEqual'
import apiGateway from '../../../services/apiGateway'
import selectWebcastTimezone from '../../selectors/selectWebcastTimezone'
import cookie from '@bt-react/libs/cookie'

export interface PlayInSlice extends PlayInObject {
  selectedIDs: string[]
  playInDetails: CommunicationType[]
}

export const playInDefaultState: PlayInSlice = {
  selectedIDs: [],
  playInDetails: [],
  currentPos: 0,
  currentPosTimestamp: '',
  endAt: null,
  initiatedBy: null,
  latestAppliedRequest: 0,
  pausedAt: null,
  remoteStatusRequest: null,
  startAt: 0,
  status: PlayInStatus.EMPTY,
  sources: []
}

export const fetchPlayInCommunicationDetails = createAsyncThunk<CommunicationType[], string[], { state: State }>('fetchPlayInCommunicationDetails', async (ids = [], { getState }) => {

  const {
    config: { channelId },
    playIn: { selectedIDs }
  } = getState()
  const { pageSizeSelected } = getState().communications
  const { timeZone } = getState().user.data

  if (!channelId || ids.length === 0) return []

  if (!ids) ids = selectedIDs

  const timezone = selectWebcastTimezone(getState()) || timeZone
  const data: CommunicationRequest = {

    channel: {
      id: channelId
    },
    ids: ids.join(','),
    limit: pageSizeSelected,
    syndication: {
      authorisation: 'approved'
    }

  }

  const response = await apiGateway.fetchCommunications(data, timezone || '')
  return response.communications

})

export const addToPlayInList = createAsyncThunk<string[], number, { state: State }>('addToPlayInList', async (playInId, { getState, dispatch }) => {

  const { roomname } = getState().config

  addEntry('PresenterService', 'PlayInAdded', `${playInId} added`, 'info', get(getState(), 'config.username', ''))
  const result = await apiGateway.addPlayInToList(roomname || '', playInId)
  dispatch(fetchPlayInCommunicationDetails(result.data.videoIds))
  return result.data.videoIds

})

export const removeFromPlayInList = createAsyncThunk<string[], number, { state: State }>('removeFromPlayInList', async (playInId, { getState, dispatch }) => {

  const { roomname } = getState().config

  addEntry('PresenterService', 'PlayInRemoved', `${playInId} removed`, 'info', get(getState(), 'config.username', ''))
  const result = await apiGateway.removePlayInFromList(roomname || '', playInId)
  dispatch(fetchPlayInCommunicationDetails(result.data.videoIds))
  return result.data.videoIds

})

// Slice

const playInSlice = createSlice({
  name: 'playIn',
  initialState: playInDefaultState,
  reducers: {
    setSources (state, { payload }: PayloadAction<{ sources: string[], initiatedBy: string | null }>) {

      state.sources = payload.sources
      state.initiatedBy = payload.initiatedBy

    },
    setStart (state, { payload }: PayloadAction<number>) {

      state.startAt = payload

    },
    setEnd (state, { payload }: PayloadAction<number | null>) {

      state.endAt = payload

    },
    setStatus (state, { payload }: PayloadAction<PlayInStatus>) {

      state.status = payload

    },
    setPaused (state, { payload }: PayloadAction<{ position: number | null, timestamp: string }>) {

      state.pausedAt = payload.position
      if (payload.position) state.currentPos = payload.position
      state.currentPosTimestamp = payload.timestamp

    },
    setCurrentPos (state, { payload }: PayloadAction<{ position: number, timestamp: string }>) {

      state.currentPos = payload.position
      state.currentPosTimestamp = payload.timestamp

    },
    setRemoteStatusRequest (state, { payload }: PayloadAction<RemoteStatusRequestInterface | null>) {

      state.remoteStatusRequest = payload

    },
    setPlayInList (state, { payload }: PayloadAction<string[]>) {

      state.selectedIDs = payload

    }
  },
  extraReducers: {
    [addToPlayInList.fulfilled.type] (state, { payload }: PayloadAction<string[]>) {

      state.selectedIDs = payload

    },
    [removeFromPlayInList.fulfilled.type] (state, { payload }: PayloadAction<string[]>) {

      state.selectedIDs = payload

    },
    [fetchPlayInCommunicationDetails.fulfilled.type] (state, { payload }: PayloadAction<CommunicationType[]>) {

      state.playInDetails = payload

    }
  }
})

// Choosing to do this as a standard thunk due to the polling spamming the redux state
export const fetchPlayInList = (roomname: string) => async (dispatch, getState: () => State) => {

  const { selectedIDs } = getState().playIn
  let playInResponse

  try {

    playInResponse = await apiGateway.getPlayInList(roomname)

  }
  catch (e) {

    addEntry('PresenterService', 'PlayInListFetching', `Error fetching playin list: ${e}.`)

  }

  if (has(playInResponse, 'data.videoIds') && !isEqual(playInResponse.data.videoIds, selectedIDs)) {

    dispatch(fetchPlayInCommunicationDetails(playInResponse.data.videoIds))
    dispatch(playInSlice.actions.setPlayInList(playInResponse.data.videoIds))

  }

}

export default playInSlice
